import {
  type Announcements,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  type ScreenReaderInstructions,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IColumn } from '@fluentui/react';
import { useTheme } from '@fluentui/react/lib/Theme';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPDefaultButton, IPIcon, IPSeparator } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapSmall, refreshIcon } from 'globalStyles';
import { CSSProperties, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyColumnOrder, buildColumnTokens, isColumnVisible, readColumnState, writeColumnState } from 'utils/columns';
import { LocalStorageKeys } from 'utils/localstorage';
import GenericModal from './GenericModal';

interface ISelectColumnsDialogProps {
  columns: IColumn[];
  isOpen: boolean;
  cacheKey: LocalStorageKeys;
  blockFirstCount?: number; //if set, will block the first n columns from being unchecked or moved (default 1)
  onClose: () => void;
  onSave: () => void;
}

const gripWidth = 24;

interface IColumnRowProps {
  column: IColumn;
  checked: boolean;
  pinned: boolean;
  onChange: (checked: boolean) => void;
}

// A row of the chooser: the grip handle (keyboard: focus it, Space, the arrow keys, Space), the checkbox, the name. A
// pinned row keeps the handle's width empty, so its checkbox lines up with the others, and cannot be unticked.
const ColumnRow = (props: IColumnRowProps & { handle?: JSX.Element }) => {
  return (
    <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall} styles={{ root: { minHeight: 32 } }}>
      {/* a Stack.Item without children renders nothing: the empty slot of a pinned row needs an element */}
      <IPStack.Item styles={{ root: { width: gripWidth, flexShrink: 0 } }}>{props.handle ?? <div />}</IPStack.Item>
      <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
        <IPCheckbox
          label={props.column.name}
          checked={props.checked}
          disabled={props.pinned}
          onChange={(_, checked) => props.onChange(checked ?? false)}
        />
      </IPStack.Item>
    </IPStack>
  );
};

const SortableColumnRow = (props: IColumnRowProps) => {
  const theme = useTheme();
  const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: props.column.key,
  });
  const style: CSSProperties = {
    position: 'relative',
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
    background: isDragging ? theme.semanticColors.bodyBackgroundHovered : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ColumnRow
        {...props}
        handle={
          <div
            ref={setActivatorNodeRef}
            title={props.column.name}
            style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: theme.semanticColors.bodySubtext }}
            {...attributes}
            {...listeners}
          >
            <IPIcon iconName="GripperBarVertical" styles={{ root: { fontSize: 16 } }} />
          </div>
        }
      />
    </div>
  );
};

/**
 * "Choose columns" of a list (PLAN-CTI-089): which columns it shows and in which order, kept in this browser under the
 * list's key (readColumnState). The first blockFirstCount columns are pinned: always shown, always first. The others
 * are dragged by their grip, with the mouse or the keyboard (@dnd-kit). "Reset to default" puts the code's order and
 * default visibility in the draft; Save keeps it, Cancel drops it.
 */
const SelectColumnsDialog = (props: ISelectColumnsDialogProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const pinnedCount = props.blockFirstCount ?? 1;
  const [order, setOrder] = useState<string[]>(() => props.columns.map((col) => col.key));
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!props.isOpen) return;

    const state = readColumnState(appContext, props.cacheKey);
    setOrder(applyColumnOrder(props.columns, state.order, pinnedCount).map((col) => col.key));
    setChecked(Object.fromEntries(props.columns.map((col) => [col.key, isColumnVisible(col, state.tokens)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const columnsByKey = new Map(props.columns.map((col) => [col.key, col]));
  const pinned = props.columns.slice(0, pinnedCount);
  const movable = order.slice(pinnedCount).filter((key) => columnsByKey.has(key));

  const reset = () => {
    setOrder(props.columns.map((col) => col.key));
    setChecked(Object.fromEntries(props.columns.map((col) => [col.key, isColumnVisible(col, [])])));
  };

  const save = () => {
    const tokens = buildColumnTokens(
      props.columns,
      props.columns.map((col) => checked[col.key] === true),
    );
    writeColumnState(appContext, props.cacheKey, { order: [...pinned.map((col) => col.key), ...movable], tokens });
    props.onSave();
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;

    const from = movable.indexOf(String(event.active.id));
    const to = movable.indexOf(String(event.over.id));
    if (from < 0 || to < 0) return;

    setOrder([...pinned.map((col) => col.key), ...arrayMove(movable, from, to)]);
  };

  // the screen reader's words while a column moves, in the user's language
  const getMoveData = (activeId: string | number, overId?: string | number) => ({
    name: columnsByKey.get(String(activeId))?.name ?? '',
    position: movable.indexOf(String(overId ?? activeId)) + pinnedCount + 1,
    count: props.columns.length,
  });
  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: t('translation:General.ColumnPicker.DragInstructions'),
  };
  const announcements: Announcements = {
    onDragStart: ({ active }) => t('translation:General.ColumnPicker.DragStarted', getMoveData(active.id)),
    onDragOver: ({ active, over }) =>
      over ? t('translation:General.ColumnPicker.DragOver', getMoveData(active.id, over.id)) : undefined,
    onDragEnd: ({ active, over }) =>
      over ? t('translation:General.ColumnPicker.DragDropped', getMoveData(active.id, over.id)) : undefined,
    onDragCancel: ({ active }) => t('translation:General.ColumnPicker.DragCancelled', getMoveData(active.id)),
  };

  const setColumnChecked = (key: string, value: boolean) => setChecked((current) => ({ ...current, [key]: value }));

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      saveDisabled={!props.columns.some((col) => checked[col.key] === true)}
      title={t('translation:General.ColumnPicker.Title')}
      subTitle={t('translation:General.ColumnPicker.SubTitle')}
      onSave={save}
      width={480}
      maxWidth={'min(90vw, 480px)'}
      height={'80vh'}
      optionalButtons={
        <IPDefaultButton
          text={t('translation:General.ColumnPicker.Reset')}
          iconProps={refreshIcon}
          // one line: the footer gives the left button only what Save and Cancel leave
          styles={{ root: { whiteSpace: 'nowrap', flexShrink: 0 } }}
          onClick={reset}
        />
      }
    >
      <IPStack verticalFill>
        <IPSeparator />
        <ScrollableStackItem>
          <IPStack verticalFill tokens={globalStackTokensGapSmall}>
            {pinned.map((col) => (
              <ColumnRow
                key={col.key}
                column={col}
                checked={checked[col.key] === true}
                pinned={true}
                onChange={(value) => setColumnChecked(col.key, value)}
              />
            ))}
            <DndContext
              accessibility={{ announcements, screenReaderInstructions }}
              collisionDetection={closestCenter}
              sensors={sensors}
              onDragEnd={onDragEnd}
            >
              <SortableContext items={movable} strategy={verticalListSortingStrategy}>
                {movable.map((key) => {
                  const col = columnsByKey.get(key) as IColumn;

                  return (
                    <SortableColumnRow
                      key={key}
                      column={col}
                      checked={checked[key] === true}
                      pinned={false}
                      onChange={(value) => setColumnChecked(key, value)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </IPStack>
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default SelectColumnsDialog;
